<script setup lang="ts">
import DownloadSection from '@/components/DownloadSection.vue'
import MobileDownloadPage from '@/components/MobileDownloadPage.vue'
import LegalDocumentPage from '@/components/LegalDocumentPage.vue'
import HeroSection from '@/components/HeroSection.vue'
import LearningScenes from '@/components/LearningScenes.vue'
import ProductStory from '@/components/ProductStory.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import SiteHeader from '@/components/SiteHeader.vue'
import SubjectGallery from '@/components/SubjectGallery.vue'
import TrustSection from '@/components/TrustSection.vue'
import EncyclopediaIntro from '@/components/EncyclopediaIntro.vue'
import { getPublicPlatformSettings, type PublicPlatformSettings } from '@/api/website'
import { useWebsiteSettings } from '@/composables/useWebsiteSettings'
import { onBeforeUnmount, onMounted, ref } from 'vue'

const platform = ref<PublicPlatformSettings | null>(null)
const props = defineProps<{ pagePath?: string }>()
const loadPlatform = async () => {
  try {
    platform.value = await getPublicPlatformSettings()
  } catch {
    platform.value = null
  }
}

const legalRoutes: Record<string, string> = {
  '/legal/user-agreement': 'user-agreement',
  '/legal/privacy-policy': 'privacy-policy',
  '/legal/children-privacy-policy': 'children-privacy-policy',
  '/legal/personal-information-collection-list': 'personal-information-collection-list',
  '/legal/third-party-information-sharing-list': 'third-party-information-sharing-list',
  '/legal/system-permission-usage': 'system-permission-usage',
}
const pagePath = props.pagePath ?? (typeof window === 'undefined' ? '/' : window.location.pathname)
const legalSlug = legalRoutes[pagePath.replace(/\/$/, '')]
const mobileDownload = pagePath.replace(/\/$/, '') === '/download/mobile'
const { settings: websiteSettings, webUrl } = useWebsiteSettings(!legalSlug && !mobileDownload)

let revealObserver: IntersectionObserver | undefined
let removeScrollListener: (() => void) | undefined

onMounted(() => {
  if (legalSlug || mobileDownload) return
  void loadPlatform()
  const revealTargets = document.querySelectorAll<HTMLElement>(
    '.manifesto, .story-step, .scene-block, .section-heading, .subject-item, .trust-list article, .platform-card',
  )
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (reduceMotion || !('IntersectionObserver' in window)) {
    revealTargets.forEach((target) => target.classList.add('is-visible'))
    return
  }

  revealTargets.forEach((target) => target.classList.add('reveal-target'))
  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('is-visible')
        revealObserver?.unobserve(entry.target)
      })
    },
    { threshold: 0.14, rootMargin: '0px 0px -8% 0px' },
  )
  revealTargets.forEach((target) => revealObserver?.observe(target))

  let ticking = false
  const updateParallax = () => {
    document.documentElement.style.setProperty(
      '--hero-parallax',
      `${Math.min(window.scrollY * 0.1, 54)}px`,
    )
    ticking = false
  }
  const handleScroll = () => {
    if (ticking) return
    ticking = true
    window.requestAnimationFrame(updateParallax)
  }

  window.addEventListener('scroll', handleScroll, { passive: true })
  removeScrollListener = () => window.removeEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  revealObserver?.disconnect()
  removeScrollListener?.()
})
</script>

<template>
  <LegalDocumentPage v-if="legalSlug" :slug="legalSlug" />
  <MobileDownloadPage v-else-if="mobileDownload" />
  <template v-else>
    <SiteHeader
      :web-url="webUrl"
      :logo-url="websiteSettings.logoUrl"
      :encyclopedia-enabled="platform?.encyclopediaEnabled === true"
    />
    <main>
      <HeroSection :image-url="websiteSettings.heroImageUrl" :web-url="webUrl" />
      <ProductStory
        :image-urls="[
          websiteSettings.storyOneImageUrl,
          websiteSettings.storyTwoImageUrl,
          websiteSettings.storyThreeImageUrl,
        ]"
      />
      <LearningScenes
        :student-image-url="websiteSettings.sceneStudentImageUrl"
        :classroom-image-url="websiteSettings.sceneClassroomImageUrl"
      />
      <SubjectGallery
        :physics-image-url="websiteSettings.subjectPhysicsImageUrl"
        :chemistry-image-url="websiteSettings.subjectChemistryImageUrl"
        :biology-image-url="websiteSettings.subjectBiologyImageUrl"
        :science-image-url="websiteSettings.subjectScienceImageUrl"
      />
      <EncyclopediaIntro v-if="platform?.encyclopediaEnabled" />
      <TrustSection />
      <DownloadSection />
    </main>
    <SiteFooter
      :web-url="webUrl"
      :website-settings="websiteSettings"
      :platform="platform"
      @retry-contact="loadPlatform"
    />
  </template>
</template>
